import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const PRIORITY_FILTERS = [
  { value: 'all', label: 'All priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function TaskFilters({
  filters,
  onFiltersChange,
  statusOptions,
  clientOptions,
  assigneeOptions,
  loading,
  onReset,
}) {
  const statusFilters = useMemo(
    () => [{ value: 'all', label: 'All statuses' }, ...statusOptions],
    [statusOptions],
  );

  const handleTextChange = (field) => (event) => {
    onFiltersChange(field, event.target.value);
  };

  const handleAutocompleteChange = (field) => (_, value) => {
    onFiltersChange(field, value ? value.id : null);
  };

  const clientValue = clientOptions.find((option) => option.id === filters.client) || null;
  const assigneeValue = assigneeOptions.find((option) => option.id === filters.assignee) || null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            value={filters.search}
            onChange={handleTextChange('search')}
            placeholder="Title, client, notes..."
            sx={{ minWidth: { xs: '100%', md: 240 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ minWidth: { xs: '100%', md: 240 } }}>
            <Autocomplete
              options={clientOptions}
              value={clientValue}
              onChange={handleAutocompleteChange('client')}
              loading={loading}
              getOptionLabel={(option) => option?.name ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All clients" />
              )}
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: 240 } }}>
            <Autocomplete
              options={assigneeOptions}
              value={assigneeValue}
              onChange={handleAutocompleteChange('assignee')}
              loading={loading}
              getOptionLabel={(option) => option?.full_name || option?.email || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Assignee" placeholder="All assignees" />
              )}
            />
          </Box>
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={handleTextChange('status')}
            sx={{ minWidth: { xs: '100%', md: 200 } }}
          >
            {statusFilters.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Priority"
            value={filters.priority}
            onChange={handleTextChange('priority')}
            sx={{ minWidth: { xs: '100%', md: 180 } }}
          >
            {PRIORITY_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={(
              <Switch
                color="primary"
                checked={filters.hideCompleted}
                onChange={(event) => onFiltersChange('hideCompleted', event.target.checked)}
              />
            )}
            label="Hide completed"
            sx={{ ml: { xs: 0, md: 2 } }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" onClick={onReset} sx={{ mt: { xs: 1, md: 0 } }}>
            Reset
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

TaskFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    client: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    assignee: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    status: PropTypes.string,
    priority: PropTypes.string,
    hideCompleted: PropTypes.bool,
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  clientOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  assigneeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  loading: PropTypes.bool,
  onReset: PropTypes.func.isRequired,
};

TaskFilters.defaultProps = {
  loading: false,
};

export default TaskFilters;
